import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useSiteData } from '../context/SiteDataContext';
import { Home, Truck, Sparkles, Square, Building2, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import CTASection from '../components/CTASection';

const iconMap = {
  Home,
  Truck,
  Sparkles,
  Square,
  Building2,
  Stairs: Building2,
};

const ServicesPage = () => {
  const { language, t } = useLanguage();
  const { services } = useSiteData();
  const serviceList = services[language];

  // Dynamically categorize services:
  // - Business services: those with 'kontor', 'trapp', 'office', or 'business' in the ID
  // - Private services: all others
  const businessKeywords = ['kontor', 'trapp', 'office', 'business', 'commercial'];
  const businessServices = serviceList.filter(s => 
    businessKeywords.some(keyword => s.id.toLowerCase().includes(keyword))
  );
  const privateServices = serviceList.filter(s => 
    !businessKeywords.some(keyword => s.id.toLowerCase().includes(keyword))
  );

  const renderServiceCard = (service) => {
    const Icon = iconMap[service.icon] || Sparkles;
    return (
      <div
        key={service.id}
        className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all hover:-translate-y-1"
      >
        <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-700 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-purple-500/20">
          <Icon className="text-white" size={28} />
        </div>
        <h3 className="text-2xl font-semibold text-gray-900 mb-3">
          {service.title}
        </h3>
        <p className="text-gray-600 mb-6 leading-relaxed">
          {service.description}
        </p>
        
        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-2 text-gray-600 text-sm">
            <CheckCircle className="text-purple-600" size={16} />
            {language === 'sv' ? 'All utrustning inkluderad' : 'All equipment included'}
          </div>
          <div className="flex items-center gap-2 text-gray-600 text-sm">
            <CheckCircle className="text-purple-600" size={16} />
            {language === 'sv' ? 'Nöjd-kund-garanti' : 'Satisfaction guarantee'}
          </div>
          <div className="flex items-center gap-2 text-gray-600 text-sm">
            <CheckCircle className="text-purple-600" size={16} />
            {language === 'sv' ? 'RUT-avdrag' : 'RUT deduction'}
          </div>
        </div>

        <div className="flex items-center justify-between pt-6 border-t border-gray-100">
          <div>
            <span className="text-sm text-gray-500">
              {language === 'sv' ? 'Från' : 'From'}
            </span>
            <p className="text-2xl font-bold text-purple-600">
              {service.basePrice} kr
              <span className="text-sm text-gray-500 font-normal ml-1">
                {service.priceUnit}
              </span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link to={`/services/${service.slug || service.id}`}>
              <Button variant="outline" className="border-purple-200 text-purple-600 hover:bg-purple-50">
                {language === 'sv' ? 'Läs mer' : 'Learn more'}
                <ArrowRight size={16} className="ml-1" />
              </Button>
            </Link>
            <Link to="/#calculator">
              <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                {language === 'sv' ? 'Beräkna pris' : 'Calculate price'}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  };

  return (
    <main>
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 right-20 w-72 h-72 bg-orange-400 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-purple-400 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t.services.title}
          </h1>
          <p className="text-xl text-purple-100 max-w-2xl mx-auto">
            {t.services.subtitle}
          </p>
        </div>
      </section>

      {/* Private Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-12">
            <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center">
              <Home className="text-white" size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{t.services.private}</h2>
              <p className="text-gray-600">
                {language === 'sv' ? 'Städtjänster för privatpersoner' : 'Cleaning services for private individuals'}
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {privateServices.map(renderServiceCard)}
          </div>
        </div>
      </section>

      {/* Business Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-12">
            <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
              <Building2 className="text-white" size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{t.services.business}</h2>
              <p className="text-gray-600">
                {language === 'sv' ? 'Städtjänster för företag och BRF' : 'Cleaning services for businesses and HOAs'}
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {businessServices.map(renderServiceCard)}
          </div>
        </div>
      </section>

      <CTASection />
    </main>
  );
};

export default ServicesPage;
